import { useRef, type ReactNode } from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown, Search, X, type LucideIcon } from "lucide-react";

import { Accordion, AccordionContent, AccordionItem } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { PopoverContent } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

/**
 * The one filter panel every page's "Filters" popover is built from — the
 * /documents filter's design, minus its dividing rules. Header (active count,
 * Clear all, search), then every dimension as an accordion section whose
 * options open in place, then yes/no dimensions as switch rows.
 *
 * Options open IN the list rather than on a pane of their own: the drill-down
 * the other pages used hid every value one click behind its dimension, and the
 * Back button was the only way to see another one.
 *
 * No `border-b` anywhere in here — sections are separated by spacing and
 * hover, not lines. Keep it that way when adding a piece.
 */

/** Width, height cap and scroll shape shared by every filter popover. */
export const FILTER_PANEL_CONTENT_CLASS =
  "flex max-h-[var(--radix-popover-content-available-height)] w-[320px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg p-0";

/** Checked/indeterminate colours of an option's checkbox. */
const OPTION_CHECKBOX_CLASS =
  "border-muted-foreground/50 data-[state=checked]:border-[#ef4129] data-[state=indeterminate]:border-[#ef4129] data-[state=checked]:bg-[#ef4129] data-[state=indeterminate]:bg-[#ef4129] data-[state=checked]:text-white data-[state=indeterminate]:text-white";

type FilterPanelContentProps = React.ComponentPropsWithoutRef<typeof PopoverContent>;

export function FilterPanelContent({
  className,
  onOpenAutoFocus,
  ...props
}: FilterPanelContentProps) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <PopoverContent
      ref={ref}
      align="start"
      collisionPadding={16}
      // Radix focuses the first tabbable element on open, which is the
      // header's Clear all — one Enter/Space on open would wipe every filter.
      // Land on the search box instead, else the first option or section.
      onOpenAutoFocus={(event) => {
        onOpenAutoFocus?.(event);
        if (event.defaultPrevented) return;
        const target = ref.current?.querySelector<HTMLElement>(
          "[data-filter-panel-search], [data-filter-panel-body] :is(button, input):not([tabindex='-1'])"
        );
        // Always take over: with nothing else to land on, Radix would fall
        // back to Clear all. The panel itself is the last resort.
        event.preventDefault();
        (target ?? ref.current)?.focus();
      }}
      className={cn(FILTER_PANEL_CONTENT_CLASS, className)}
      {...props}
    />
  );
}

interface FilterPanelHeaderProps {
  /** "Active filters" */
  title: string;
  activeCount: number;
  clearLabel: string;
  onClearAll: () => void;
  /** Defaults to `activeCount === 0`. */
  clearDisabled?: boolean;
  /** Omit to render the header without a search box. */
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    clearLabel: string;
  };
}

export function FilterPanelHeader({
  title,
  activeCount,
  clearLabel,
  onClearAll,
  clearDisabled = activeCount === 0,
  search,
}: FilterPanelHeaderProps) {
  return (
    <div className="shrink-0 px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold">
          {title} · {activeCount}
        </span>
        <button
          type="button"
          onClick={onClearAll}
          disabled={clearDisabled}
          className="shrink-0 text-xs font-medium text-[#ef4129] underline-offset-2 hover:underline disabled:cursor-default disabled:text-muted-foreground disabled:no-underline"
        >
          {clearLabel}
        </button>
      </div>
      {search && (
        <div className="relative mt-2">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search.value}
            onChange={(event) => search.onChange(event.target.value)}
            placeholder={search.placeholder}
            aria-label={search.placeholder}
            data-filter-panel-search
            className="h-8 pl-8 pr-8 text-xs"
          />
          {search.value.length > 0 && (
            <button
              type="button"
              onClick={() => search.onChange("")}
              aria-label={search.clearLabel}
              className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Scroll body under the header. */
export function FilterPanelBody({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      data-filter-panel-body
      className={cn("min-h-0 flex-1 overflow-y-auto overscroll-contain pb-1", className)}
    >
      {children}
    </div>
  );
}

interface FilterSectionsProps {
  /** The open section, or null for none. */
  value: string | null;
  onValueChange: (value: string | null) => void;
  children: ReactNode;
}

/** One-open-at-a-time accordion of filter dimensions. */
export function FilterSections({ value, onValueChange, children }: FilterSectionsProps) {
  return (
    <Accordion
      type="single"
      collapsible
      // `""`, never `undefined`: Radix reads an undefined `value` as
      // uncontrolled and falls back to its own internal state, which still
      // holds the section that was just closed — so it would never collapse.
      value={value ?? ""}
      onValueChange={(next) => onValueChange(next || null)}
    >
      {children}
    </Accordion>
  );
}

interface FilterSectionProps {
  value: string;
  icon: LucideIcon;
  label: string;
  /** Selected options in this section; drives the badge and the clear X. */
  selectedCount: number;
  /** Clears this section. Omit to show no X. */
  onClear?: () => void;
  clearLabel: string;
  /** Extra content on the trigger row, before the badge (e.g. a warning count). */
  adornment?: ReactNode;
  children: ReactNode;
  contentClassName?: string;
  /** One option at a time: the rows are radios, grouped under the label. */
  singleChoice?: boolean;
}

export function FilterSection({
  value,
  icon: Icon,
  label,
  selectedCount,
  onClear,
  clearLabel,
  adornment,
  children,
  contentClassName,
  singleChoice = false,
}: FilterSectionProps) {
  return (
    <AccordionItem value={value} className="border-b-0">
      <AccordionPrimitive.Header className="relative flex">
        <AccordionPrimitive.Trigger className="flex flex-1 items-center gap-2 px-3 py-2.5 pr-2 text-xs font-medium hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none [&[data-state=open]_.filter-chevron]:rotate-180">
          <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 truncate">{label}</span>
          {adornment}
          {selectedCount > 0 && (
            <span className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-[#ef4129] px-1 text-[10px] font-semibold text-white">
              {selectedCount}
            </span>
          )}
          <span className="ml-auto h-6 w-6 shrink-0" aria-hidden="true" />
          <ChevronDown className="filter-chevron h-4 w-4 shrink-0 transition-transform duration-200" />
        </AccordionPrimitive.Trigger>
        {selectedCount > 0 && onClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label={`${clearLabel}: ${label}`}
            className="absolute right-8 top-1/2 z-10 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </AccordionPrimitive.Header>
      <AccordionContent
        className={cn("max-h-[200px] overflow-y-auto overscroll-contain pb-1", contentClassName)}
      >
        {singleChoice ? (
          <div role="radiogroup" aria-label={label}>
            {children}
          </div>
        ) : (
          children
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

interface FilterOptionRowProps {
  label: ReactNode;
  /** Accessible name of the checkbox — the label text, ideally prefixed with its dimension. */
  ariaLabel: string;
  checked: boolean;
  indeterminate?: boolean;
  onToggle: () => void;
  /** Shown in parentheses on the right. */
  count?: number;
  /** Dimension name under the label — search results, where rows of several dimensions mix. */
  sublabel?: string;
  /** Leading visual after the checkbox (avatar, icon, status badge). */
  leading?: ReactNode;
  indent?: boolean;
  disabled?: boolean;
  className?: string;
  /** A one-of-many choice: announced as a radio, drawn as the same checkbox. */
  single?: boolean;
}

export function FilterOptionRow({
  label,
  ariaLabel,
  checked,
  indeterminate = false,
  onToggle,
  count,
  sublabel,
  leading,
  indent = false,
  disabled = false,
  className,
  single = false,
}: FilterOptionRowProps) {
  return (
    <div
      className={cn(
        "flex min-h-8 items-center gap-2 px-3 text-xs",
        disabled ? "opacity-50" : "hover:bg-muted/60",
        indent && "pl-6",
        className
      )}
    >
      <Checkbox
        checked={checked}
        indeterminate={indeterminate}
        onCheckedChange={onToggle}
        disabled={disabled}
        aria-label={ariaLabel}
        // Spread, never `role={undefined}`: that would erase Radix's own role.
        {...(single ? { role: "radio" } : {})}
        className={OPTION_CHECKBOX_CLASS}
      />
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        // The checkbox is the row's one tab stop; this button only widens the
        // pointer target, so it stays out of the tab order.
        tabIndex={-1}
        className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left focus-visible:outline-none disabled:cursor-not-allowed"
      >
        {leading}
        <span className="min-w-0 flex-1 truncate">
          <span className="block truncate">{label}</span>
          {sublabel && (
            <span className="block truncate text-[10px] leading-3 text-muted-foreground">
              {sublabel}
            </span>
          )}
        </span>
        {count != null && (
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">({count})</span>
        )}
      </button>
    </div>
  );
}

interface FilterSwitchRowProps {
  icon: LucideIcon;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** Extra content before the switch (e.g. a count badge). */
  adornment?: ReactNode;
}

/** A yes/no dimension: the row is the switch's hit area. */
export function FilterSwitchRow({
  icon: Icon,
  label,
  checked,
  onCheckedChange,
  adornment,
}: FilterSwitchRowProps) {
  return (
    <label className="flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-xs hover:bg-muted">
      <span className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        {label}
      </span>
      <span className="flex items-center gap-1.5">
        {adornment}
        <Switch
          checked={checked}
          onCheckedChange={onCheckedChange}
          aria-label={label}
          className="origin-right scale-[0.7]"
        />
      </span>
    </label>
  );
}

/** Empty message for a search with no hits, or a section with no options. */
export function FilterPanelEmpty({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-16 items-center justify-center px-3 py-4 text-center text-xs text-muted-foreground">
      {children}
    </div>
  );
}

/** Note under a section's options (e.g. why some are disabled). */
export function FilterSectionNote({ children }: { children: ReactNode }) {
  return <p className="px-3 pt-1 text-[10px] leading-snug text-muted-foreground">{children}</p>;
}
